import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Chip, Drawer, IconButton, Button, CircularProgress, Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import api from '../adapters/api';

dayjs.locale('th');

const fmt2 = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const ACTION_CONFIG = {
  CREATE:  { label: 'สร้างใหม่', bg: '#e8f5e9', color: '#2e7d32' },
  SAVE:    { label: 'บันทึก',   bg: '#e3f2fd', color: '#1565c0' },
  UPDATE:  { label: 'แก้ไข',    bg: '#fff3e0', color: '#e65100' },
  DELETE:  { label: 'ลบ',       bg: '#ffebee', color: '#b71c1c' },
  RESTORE: { label: 'กู้คืน',   bg: '#e0f7fa', color: '#00695c' },
};

const PRIORITY_CONFIG = {
  urgent:  { emoji: '🔴', label: 'ด่วนมาก', color: '#b71c1c', bg: '#ffebee' },
  high:    { emoji: '🟠', label: 'ด่วน',    color: '#e65100', bg: '#fff3e0' },
  today:   { emoji: '🟡', label: 'วันนี้',   color: '#f57f17', bg: '#fffde7' },
  waiting: { emoji: '🔵', label: 'รอ',       color: '#1565c0', bg: '#e3f2fd' },
};

export default function ActivityLog() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerItem, setDrawerItem] = useState(null);
  const [snapshotData, setSnapshotData] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/activity');
      setActivities(res.data.activities || []);
    } catch (err) {
      console.error('fetchActivities error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const handleRowClick = useCallback((params) => {
    setDrawerItem(params.row);
    setSnapshotData(null);
  }, []);

  useEffect(() => {
    if (!drawerItem || drawerItem.action !== 'DELETE' || !drawerItem.has_snapshot) return;
    setSnapshotLoading(true);
    api.get(`/api/activity/snapshot/${drawerItem.id}`)
      .then(res => setSnapshotData(res.data.snapshot))
      .catch(err => console.error('snapshot fetch error:', err))
      .finally(() => setSnapshotLoading(false));
  }, [drawerItem]);

  const columns = [
    {
      field: 'acted_at',
      headerName: 'วันที่/เวลา',
      width: 160,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontSize: 13 }}>
          {value ? dayjs(value).format('DD/MM/BBBB HH:mm') : '—'}
        </Typography>
      ),
    },
    {
      field: 'action',
      headerName: 'การกระทำ',
      width: 110,
      renderCell: ({ value }) => {
        const cfg = ACTION_CONFIG[value] || { label: value, bg: '#f5f5f5', color: '#616161' };
        return (
          <Chip size="small" label={cfg.label}
            sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: 12, height: 22 }}
          />
        );
      },
    },
    {
      field: 'doc_type',
      headerName: 'ประเภท',
      width: 80,
      renderCell: ({ value }) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
          {value === 'DRAFT' ? 'Draft' : value === 'QT' ? 'ใบเสนอราคา' : value || '—'}
        </Typography>
      ),
    },
    {
      field: 'doc_ref',
      headerName: 'เลขที่เอกสาร',
      flex: 1,
      minWidth: 160,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontSize: 13 }}>{value || '—'}</Typography>
      ),
    },
  ];

  const closeDrawer = () => setDrawerItem(null);

  const navigateToDoc = () => {
    if (!drawerItem) return;
    closeDrawer();
    if (drawerItem.doc_type === 'DRAFT' && drawerItem.cart_guid) {
      navigate(`/orderlist?tab=DRAFT&highlight=${drawerItem.cart_guid}`);
    } else if (drawerItem.doc_type === 'QT' && drawerItem.doc_ref) {
      navigate(`/orderlist?tab=QT&search=${drawerItem.doc_ref}`);
    } else {
      navigate('/orderlist');
    }
  };

  const handleRestoreDraft = async () => {
    if (!drawerItem?.cart_guid || restoreLoading) return;

    const confirm = await Swal.fire({
      icon: 'question',
      title: 'กู้คืน Draft?',
      text: `ต้องการกู้คืน Draft ${drawerItem.doc_ref || ''} ใช่หรือไม่`,
      showCancelButton: true,
      confirmButtonColor: '#2e7d32',
      cancelButtonColor: '#757575',
      confirmButtonText: 'กู้คืน',
      cancelButtonText: 'ยกเลิก',
    });
    if (!confirm.isConfirmed) return;

    setRestoreLoading(true);
    try {
      await api.post(`/api/draft/${drawerItem.cart_guid}/restore`);
      const docRef = drawerItem.doc_ref;
      const restoredGuid = drawerItem.cart_guid;
      closeDrawer();
      await fetchActivities();
      await Swal.fire({
        icon: 'success',
        title: 'กู้คืน Draft สำเร็จ',
        text: `Draft ${docRef} ถูกกู้คืนแล้ว`,
        timer: 1500,
        showConfirmButton: false,
      });
      navigate(`/orderlist?tab=DRAFT&highlight=${restoredGuid}`);
    } catch (err) {
      console.error('restore draft error:', err);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถกู้คืน Draft ได้',
        confirmButtonColor: '#1976d2',
        confirmButtonText: 'ตกลง',
      });
    } finally {
      setRestoreLoading(false);
    }
  };

  const drawerCfg = drawerItem ? ACTION_CONFIG[drawerItem.action] : null;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>ประวัติการใช้งาน</Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'กำลังโหลด…' : `${activities.length} รายการล่าสุด (สูงสุด 100 รายการ)`}
          </Typography>
        </Box>
        <Tooltip title="รีเฟรช">
          <span>
            <IconButton onClick={fetchActivities} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Table */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          rows={activities}
          columns={columns}
          loading={loading}
          onRowClick={handleRowClick}
          getRowId={(row) => row.id}
          density="compact"
          disableColumnMenu
          disableRowSelectionOnClick
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50' },
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
          }}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary" variant="body2">ไม่พบประวัติการใช้งาน</Typography>
              </Box>
            ),
          }}
        />
      </Box>

      {/* Detail drawer */}
      <Drawer
        anchor="right"
        open={Boolean(drawerItem)}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 480 } } }}
      >
        {drawerItem && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Box sx={{
              px: 2.5, py: 1.75,
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0,
            }}>
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                  {drawerItem.doc_ref || '—'}
                </Typography>
                {drawerCfg && (
                  <Chip size="small" label={drawerCfg.label}
                    sx={{ mt: 0.5, bgcolor: drawerCfg.bg, color: drawerCfg.color, border: `1px solid ${drawerCfg.color}30`, fontWeight: 600 }}
                  />
                )}
              </Box>
              <IconButton size="small" onClick={closeDrawer} sx={{ mt: 0.25 }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
              {drawerItem.action === 'DELETE' ? (
                snapshotLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : snapshotData ? (
                  <SnapshotView snapshot={snapshotData} />
                ) : (
                  <Typography color="text.secondary" variant="body2">ไม่พบข้อมูล snapshot</Typography>
                )
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    รายละเอียด
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2.5, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', alignItems: 'baseline' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ประเภทเอกสาร</Typography>
                    <Typography variant="body2">
                      {drawerItem.doc_type === 'DRAFT' ? 'Draft' : drawerItem.doc_type === 'QT' ? 'ใบเสนอราคา' : drawerItem.doc_type || '—'}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>เลขที่เอกสาร</Typography>
                    <Typography variant="body2" fontWeight={600}>{drawerItem.doc_ref || '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>เวลา</Typography>
                    <Typography variant="body2">{dayjs(drawerItem.acted_at).format('DD/MM/BBBB HH:mm')}</Typography>
                  </Box>
                </>
              )}
            </Box>

            {/* Footer */}
            {drawerItem.action === 'DELETE' ? (
              drawerItem.cart_guid && (
                <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                  <Tooltip title={drawerItem.doc_exists ? 'ถูกกู้คืนแล้ว' : ''} disableHoverListener={!drawerItem.doc_exists}>
                    <span style={{ display: 'block' }}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<RestoreFromTrashIcon />}
                        onClick={handleRestoreDraft}
                        disabled={restoreLoading || Boolean(drawerItem.doc_exists)}
                        sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                      >
                        {restoreLoading ? 'กำลังกู้คืน...' : drawerItem.doc_exists ? 'ถูกกู้คืนแล้ว' : 'กู้คืน Draft'}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              )
            ) : (
              <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                {(() => {
                  const knownType = drawerItem.doc_type === 'DRAFT' || drawerItem.doc_type === 'QT';
                  const gone = drawerItem.doc_exists === false;
                  const tooltipTitle = gone ? 'เอกสารไม่มีอยู่แล้ว' : !knownType ? 'ประเภทเอกสารนี้ไม่รองรับการนำทาง' : '';
                  return (
                    <Tooltip title={tooltipTitle} disableHoverListener={!tooltipTitle}>
                      <span style={{ display: 'block' }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<OpenInNewIcon />}
                          onClick={navigateToDoc}
                          disabled={gone || !knownType}
                        >
                          ไปที่เอกสาร
                        </Button>
                      </span>
                    </Tooltip>
                  );
                })()}
              </Box>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

function SnapshotView({ snapshot }) {
  const priority = PRIORITY_CONFIG[snapshot.draft_priority] || null;

  return (
    <>
      {/* Deletion info */}
      <Box sx={{ bgcolor: '#fbe9e7', borderRadius: 1.5, p: 1.5, mb: 2.5, border: '1px solid #ffcdd2' }}>
        <Typography variant="caption" sx={{ color: '#b71c1c', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          ข้อมูลการลบ
        </Typography>
        <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', alignItems: 'baseline' }}>
          <Typography variant="body2" color="text.secondary">ลบโดย</Typography>
          <Typography variant="body2" fontWeight={600}>
            {snapshot.deleted_by || '—'}
            {snapshot.deleted_by_code && snapshot.deleted_by_code !== snapshot.deleted_by
              ? ` (${snapshot.deleted_by_code})` : ''}
          </Typography>

          <Typography variant="body2" color="text.secondary">วันที่ลบ</Typography>
          <Typography variant="body2" fontWeight={600}>
            {snapshot.deleted_at ? dayjs(snapshot.deleted_at).format('DD/MM/BBBB HH:mm') : '—'}
          </Typography>
        </Box>
      </Box>

      {/* Draft info */}
      <Typography variant="caption" color="text.secondary"
        sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        ข้อมูล Draft
      </Typography>
      <Box sx={{ mt: 1, mb: 2.5, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', alignItems: 'baseline' }}>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ลูกค้า</Typography>
        <Typography variant="body2" fontWeight={500}>{snapshot.customer_name || '—'}</Typography>

        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>พนักงานขาย</Typography>
        <Typography variant="body2">{snapshot.sale_name || snapshot.sale_code || '—'}</Typography>

        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>วันที่เอกสาร</Typography>
        <Typography variant="body2">{snapshot.doc_date ? dayjs(snapshot.doc_date).format('DD/MM/BBBB') : '—'}</Typography>

        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>จำนวนเงิน</Typography>
        <Typography variant="body2" fontWeight={700} color="success.dark">
          {fmt2(parseFloat(snapshot.total_sum_amount || 0))} บาท
        </Typography>

        {parseFloat(snapshot.total_weight || 0) > 0 && <>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>น้ำหนักรวม</Typography>
          <Typography variant="body2">{fmt2(parseFloat(snapshot.total_weight || 0))} กก.</Typography>
        </>}

        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>สร้างเมื่อ</Typography>
        <Typography variant="body2">
          {snapshot.create_datetime ? dayjs(snapshot.create_datetime).format('DD/MM/BBBB HH:mm') : '—'}
        </Typography>

        {snapshot.remark && <>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>หมายเหตุบิล</Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{snapshot.remark}</Typography>
        </>}

        {snapshot.draft_note && <>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>หมายเหตุ Draft</Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{snapshot.draft_note}</Typography>
        </>}

        {priority && <>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ความสำคัญ</Typography>
          <Chip size="small" label={`${priority.emoji} ${priority.label}`}
            sx={{ fontSize: 11, height: 20, bgcolor: priority.bg, color: priority.color,
              border: `1px solid ${priority.color}40`, fontWeight: 600, px: 0.25, justifySelf: 'start' }}
          />
        </>}
      </Box>

      {/* Item list */}
      {Array.isArray(snapshot.details) && snapshot.details.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="caption" color="text.secondary"
              sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              รายการสินค้า
            </Typography>
            <Chip size="small" label={`${snapshot.details.length} รายการ`}
              sx={{ height: 18, fontSize: 11, bgcolor: 'grey.100' }}
            />
          </Box>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', mb: 2 }}>
            {snapshot.details.map((d, i) => (
              <Box key={i} sx={{
                px: 1.5, py: 1,
                bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50',
                borderBottom: i < snapshot.details.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}>
                <Typography variant="body2" sx={{ lineHeight: 1.4, mb: 0.5, wordBreak: 'break-word' }}>
                  {d.item_name}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {fmt2(parseFloat(d.qty || 0))} {d.unit_code} × {fmt2(parseFloat(d.price || 0))} บาท
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="success.dark">
                    {fmt2(parseFloat(d.sum_amount || 0))} บาท
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">รวม {snapshot.details.length} รายการ</Typography>
            <Typography variant="body1" fontWeight={700} color="success.dark">
              {fmt2(parseFloat(snapshot.total_sum_amount || 0))} บาท
            </Typography>
          </Box>
        </>
      )}
    </>
  );
}
